import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react";
import { useAuth } from "./AuthContext.tsx";
import {
  userSettingsStorage,
  progressStorage,
  syncStorage,
  UserSettings,
  isLocalStorageAvailable,
} from "../lib/localStorage";
import type { ProgressTracking, StudySession } from "../types/database.types";

interface AppDataContextType {
  // Settings
  settings: UserSettings;
  updateSettings: (updates: Partial<UserSettings>) => void;
  saveSettings: () => Promise<boolean>;

  // Progress data
  cachedProgress: ProgressTracking[] | null;
  updateCachedProgress: (progress: ProgressTracking[]) => void;
  clearProgressCache: () => void;

  // Study Sessions
  cachedStudySessions: StudySession[] | null;
  updateCachedStudySessions: (sessions: StudySession[]) => void;

  // Sync status
  lastSync: number | null;
  updateLastSync: (timestamp?: number) => void;

  // Utility
  clearAllData: () => void;
  isDataLoaded: boolean;
}

interface AppDataProviderProps {
  children: ReactNode;
}

const defaultSettings: UserSettings = {
  emailNotifications: true,
  studyReminders: true,
  publicProfile: false,
  shareProgress: true,
  language: "en",
  timezone: "UTC",

  // Profile & Learning defaults
  full_name: "",
  gradeLevel: "high-school",
  learningStyle: "balanced",
  studyGoal: "general",
  adaptiveDifficulty: true,

  // Notifications & Privacy defaults
  achievementAlerts: true,
  profilePublic: false,
  showStats: true,

  // Quiz defaults
  objectiveQuestionsCount: 5,
  subjectiveQuestionsCount: 5,
};

const AppDataContext = createContext<AppDataContextType | undefined>(undefined);

/**
 * Global application data context with localStorage persistence
 * Manages user settings and cached progress
 */
export const AppDataProvider = ({ children }: AppDataProviderProps) => {
  const { user, isAuthenticated } = useAuth();

  // State
  const [settings, setSettings] = useState<UserSettings>(defaultSettings);
  const [cachedProgress, setCachedProgress] = useState<ProgressTracking[] | null>(null);
  const [cachedStudySessions, setCachedStudySessions] = useState<StudySession[] | null>(null);
  const [lastSync, setLastSync] = useState<number | null>(null);
  const [isDataLoaded, setIsDataLoaded] = useState(false);

  /**
   * Load all data from localStorage on mount and when user changes
   */
  useEffect(() => {
    const loadData = async () => {
      if (!isLocalStorageAvailable()) {
        setIsDataLoaded(true);
        return;
      }

      try {
        // Load settings
        const savedSettings = userSettingsStorage.load();
        if (savedSettings) {
          setSettings({ ...defaultSettings, ...savedSettings });
        }

        // Load cached data only if user is authenticated
        if (user?.id) {
          const progress = progressStorage.load(user.id);
          if (progress) {
            setCachedProgress(progress);
          }
        }

        // Load sync timestamp
        const syncTime = syncStorage.getLastSync();
        setLastSync(syncTime);

      } catch (error) {
        console.error("Failed to load app data:", error);
      } finally {
        setIsDataLoaded(true);
      }
    };

    loadData();
  }, [user?.id]);

  /**
   * Clear all cached data when user logs out
   */
  useEffect(() => {
    if (!isAuthenticated && isDataLoaded) {
      setCachedProgress(null);
      setLastSync(null);
    }
  }, [isAuthenticated, isDataLoaded]);

  /**
   * Update settings in state
   */
  const updateSettings = useCallback((updates: Partial<UserSettings>) => {
    setSettings(prev => ({ ...prev, ...updates }));
  }, []);

  /**
   * Save settings to localStorage
   */
  const saveSettings = useCallback(async (): Promise<boolean> => {
    if (!isLocalStorageAvailable()) return false;

    const success = userSettingsStorage.save(settings);
    if (success) {
      updateLastSync();
    }
    return success;
  }, [settings]);

  /**
   * Update cached progress data
   */
  const updateCachedProgress = useCallback((progress: ProgressTracking[]) => {
    setCachedProgress(progress);
    if (user?.id && isLocalStorageAvailable()) {
      progressStorage.save(progress, user.id);
      updateLastSync();
    }
  }, [user?.id]);

  /**
   * Clear progress cache
   */
  const clearProgressCache = useCallback(() => {
    setCachedProgress(null);
    if (isLocalStorageAvailable()) {
      progressStorage.clear();
    }
  }, []);

  /**
   * Update last sync timestamp
   */
  const updateLastSync = useCallback((timestamp: number = Date.now()) => {
    setLastSync(timestamp);
    if (isLocalStorageAvailable()) {
      syncStorage.saveLastSync(timestamp);
    }
  }, []);

  /**
   * Clear all user data
   */
  const clearAllData = useCallback(() => {
    setSettings(defaultSettings);
    setCachedProgress(null);
    setCachedStudySessions(null);
    setLastSync(null);

    if (isLocalStorageAvailable()) {
      userSettingsStorage.clear();
      progressStorage.clear();
      syncStorage.clear();
    }
  }, []);

  const value: AppDataContextType = {
    // Settings
    settings,
    updateSettings,
    saveSettings,

    // Progress data
    cachedProgress,
    updateCachedProgress,
    clearProgressCache,

    // Study Sessions
    cachedStudySessions,
    updateCachedStudySessions: setCachedStudySessions,

    // Sync status
    lastSync,
    updateLastSync,

    // Utility
    clearAllData,
    isDataLoaded,
  };

  return (
    <AppDataContext.Provider value={value}>
      {children}
    </AppDataContext.Provider>
  );
};

/**
 * Custom hook to use the AppDataContext
 */
export const useAppData = (): AppDataContextType => {
  const context = useContext(AppDataContext);

  if (context === undefined) {
    throw new Error(
      "useAppData must be used within an AppDataProvider. " +
      "Make sure your component is wrapped with <AppDataProvider>."
    );
  }

  return context;
};

export default AppDataContext;
